/**
 * Applying a `:::` marker to a selection — the pure half of #240.
 *
 * Kept out of the component so it can be tested without a browser or a
 * CodeMirror instance: given the document text and a selection, it returns the
 * replacement text. The component does the cm.replaceSelection() and nothing
 * else.
 *
 * The vocabulary MUST match picaluna-v3's renderMarkdown:
 *   block markers   ::: <names> … :::   or a name alone on its own line
 *   inline spans    ::: colour-x … :::  mid-line, colours ONLY
 * Alignment has no meaning on an inline span (text-align is a property of the
 * block box), so an alignment button always acts on whole lines.
 */

export type MarkerKind = 'colour' | 'align';

export interface Selection {
  /** The whole document. */
  text: string;
  /** Character offsets of the selection within `text`. */
  from: number;
  to: number;
}

export interface TransformResult {
  /** Replacement for the SELECTED range (from..to). */
  replacement: string;
  /** What the selection should cover afterwards, relative to `from`. */
  selectFrom: number;
  selectTo: number;
}

/** Marker names the renderer knows. Additive only — see renderMarkdown.ts. */
export const COLOUR_MARKERS = [
  { label: 'Forest Green', value: 'colour-forest' },
  { label: 'Picaluna Blue', value: 'colour-blue' },
  { label: 'Burnt Orange', value: 'colour-orange' },
  { label: 'Off White', value: 'colour-off-white' },
  { label: 'Brand Black', value: 'colour-brand-black' },
  { label: 'White', value: 'colour-white' },
];

export const ALIGN_MARKERS = [
  { label: 'Centre', value: 'center' },
  { label: 'Left', value: 'left' },
  { label: 'Right', value: 'right' },
];

const ALL_NAMES = [...COLOUR_MARKERS, ...ALIGN_MARKERS].map((m) => m.value);

/** Expand a selection to whole lines — what an alignment marker must wrap. */
export function expandToLines(text: string, from: number, to: number): { from: number; to: number } {
  let s = from;
  let e = to;
  while (s > 0 && text[s - 1] !== '\n') s--;
  while (e < text.length && text[e] !== '\n') e++;
  return { from: s, to: e };
}

/**
 * Is this selection already wrapped in a marker of `kind`? Returns the names
 * found, so pressing the same button twice can REMOVE it the way Bold toggles
 * rather than nesting a second marker inside the first.
 */
export function existingMarker(
  block: string
): { names: string[]; inner: string; inline: boolean } | null {
  const trimmed = block.trim();

  // Block form: ::: names \n … \n :::
  const blockRe = /^:::[ \t]+([a-z-]+(?:[ \t]+[a-z-]+)*)[ \t]*\n([\s\S]*?)\n:::$/;
  const b = blockRe.exec(trimmed);
  if (b && b[1].split(/\s+/).every((n) => ALL_NAMES.includes(n))) {
    return { names: b[1].split(/\s+/), inner: b[2], inline: false };
  }

  // One-line form: ::: names … :::
  const lineRe = /^:::[ \t]+([a-z-]+(?:[ \t]+[a-z-]+)*)[ \t]+(.*?)[ \t]*:::$/;
  const l = lineRe.exec(trimmed);
  if (l && l[1].split(/\s+/).every((n) => ALL_NAMES.includes(n))) {
    return { names: l[1].split(/\s+/), inner: l[2], inline: true };
  }

  return null;
}

/**
 * Apply (or toggle off) a marker.
 *
 * `kind` decides the shape, not the name: a colour on a mid-line selection is
 * an inline span; an alignment is always whole lines, because an inline
 * text-align does nothing and a button that silently does nothing is the
 * failure mode this whole feature exists to remove.
 */
export function applyMarker(sel: Selection, name: string, kind: MarkerKind): TransformResult {
  const { text } = sel;
  const wholeLine = kind === 'align';
  const range = markerRange(text, sel.from, sel.to, kind);
  const block = text.slice(range.from, range.to);
  const existing = existingMarker(block);

  // Pressing the same marker again removes it.
  if (existing && existing.names.includes(name)) {
    const remaining = existing.names.filter((n) => n !== name);
    const replacement = remaining.length
      ? wrap(existing.inner, remaining, existing.inline)
      : existing.inner;
    return { replacement, selectFrom: 0, selectTo: replacement.length };
  }

  // A different marker of the same kind REPLACES it, so colours do not stack
  // into `::: colour-blue colour-orange` where the first silently wins.
  if (existing) {
    const kept = existing.names.filter((n) => !isKind(n, kind));
    const replacement = wrap(existing.inner, [...kept, name], existing.inline);
    return { replacement, selectFrom: 0, selectTo: replacement.length };
  }

  /* Whitespace at the edges of a selection stays OUTSIDE the marker. A
     double-click selects "395 " including the trailing space, and wrapping
     that produced `::: colour-orange 395  :::` — two spaces before the close,
     which the renderer's regex still matches but which reads as a typo in the
     editor and drifts if someone edits around it. */
  const lead = block.match(/^\s*/)?.[0] ?? '';
  const trail = block.match(/\s*$/)?.[0] ?? '';
  const core = block.slice(lead.length, block.length - trail.length);
  if (!core) return { replacement: block, selectFrom: 0, selectTo: block.length };

  const inline = !wholeLine && !core.includes('\n');
  const wrapped = wrap(core, [name], inline);
  const replacement = lead + wrapped + trail;
  return { replacement, selectFrom: lead.length, selectTo: lead.length + wrapped.length };
}

/**
 * Remove every `kind` name from the marker around the selection - the menu's
 * Clear option, which puts the text back to the section's default colour or
 * alignment. Names of the other kind on the same marker stay, so clearing the
 * colour of `::: center colour-blue` leaves `::: center`.
 *
 * Returns null when there is nothing of that kind to clear, so the menu can
 * leave the option out rather than offer a button that does nothing.
 */
export function clearMarker(sel: Selection, kind: MarkerKind): TransformResult | null {
  const range = markerRange(sel.text, sel.from, sel.to, kind);
  const existing = existingMarker(sel.text.slice(range.from, range.to));
  if (!existing || !existing.names.some((n) => isKind(n, kind))) return null;

  const kept = existing.names.filter((n) => !isKind(n, kind));
  const replacement = kept.length ? wrap(existing.inner, kept, existing.inline) : existing.inner;
  return { replacement, selectFrom: 0, selectTo: replacement.length };
}

/**
 * The range a marker button acts on: whole lines for alignment, grown to the
 * enclosing marker when there is one. Toggling and clearing work off the
 * MARKER, not the selection - someone who wrapped text and wants it back does
 * not reselect the `:::` delimiters, they select the words or leave the cursor
 * there. Without this, pressing a button twice nested a second marker inside
 * the first. The component replaces exactly this range with the result.
 */
export function markerRange(
  text: string,
  from: number,
  to: number,
  kind: MarkerKind
): { from: number; to: number } {
  const range = kind === 'align' ? expandToLines(text, from, to) : { from, to };
  return enclosingMarker(text, range.from, range.to) ?? range;
}

function isKind(name: string, kind: MarkerKind): boolean {
  return kind === 'colour' ? name.startsWith('colour-') : !name.startsWith('colour-');
}

/** The marker's text form. Multi-line content always takes the block shape. */
function wrap(inner: string, names: string[], inline: boolean): string {
  const n = names.join(' ');
  if (inline && !inner.includes('\n')) return `::: ${n} ${inner} :::`;
  return `::: ${n}\n${inner}\n:::`;
}

/** The range an align button should act on, for the caller to re-select. */
export function alignRange(text: string, from: number, to: number) {
  return expandToLines(text, from, to);
}


/**
 * The marker surrounding a range, if any — so a toggle acts on the whole
 * `::: … :::` rather than on whatever words happen to be selected inside it.
 *
 * Searches the current line for an inline marker, then the whole document for
 * a block one. Returns null when the range is not inside a marker at all.
 */
export function enclosingMarker(
  text: string,
  from: number,
  to: number
): { from: number; to: number } | null {
  // Inline: scan the line the selection starts on.
  const lineStart = text.lastIndexOf('\n', Math.max(0, from - 1)) + 1;
  let lineEnd = text.indexOf('\n', to);
  if (lineEnd === -1) lineEnd = text.length;
  const line = text.slice(lineStart, lineEnd);
  const inlineRe = /:::[ \t]+[a-z-]+(?:[ \t]+[a-z-]+)*[ \t]+.*?[ \t]*:::/g;
  let m: RegExpExecArray | null;
  while ((m = inlineRe.exec(line))) {
    const s = lineStart + m.index;
    const e = s + m[0].length;
    if (from >= s && to <= e) return { from: s, to: e };
  }

  // Block: ::: names on its own line through the next bare :::
  const blockRe = /^:::[ \t]+[a-z-]+(?:[ \t]+[a-z-]+)*[ \t]*\n[\s\S]*?\n:::$/gm;
  while ((m = blockRe.exec(text))) {
    const s = m.index;
    const e = s + m[0].length;
    if (from >= s && to <= e) return { from: s, to: e };
  }
  return null;
}

/**
 * Bold / italic on a selection (the editor's B and I, and the hero headline's): wrap it in `mark`, or unwrap it when
 * it is already wrapped, inside the selection or just outside it. Whitespace at the selection's edges stays OUTSIDE
 * the markers, because a double-clicked word carries its trailing space and `in*** October 2016 ***with` never opens
 * emphasis — markdown requires the delimiter to touch the word. Null when there is nothing but whitespace to wrap.
 */
export function toggleEmphasis(
  text: string,
  from: number,
  to: number,
  mark: string
): { start: number; end: number; replacement: string } | null {
  const raw = text.slice(from, to);
  const lead = raw.match(/^\s*/)?.[0] ?? '';
  const trail = raw.match(/\s*$/)?.[0] ?? '';
  const core = raw.slice(lead.length, raw.length - trail.length);
  if (!core) return null;

  const inner =
    core.startsWith(mark) && core.endsWith(mark) && core.length > mark.length * 2 ? core.slice(mark.length, -mark.length) : null;
  const outside = text.slice(Math.max(0, from - mark.length), from) === mark && text.slice(to, to + mark.length) === mark;

  if (inner !== null) return { start: from, end: to, replacement: lead + inner + trail };
  if (outside) return { start: from - mark.length, end: to + mark.length, replacement: raw };
  return { start: from, end: to, replacement: `${lead}${mark}${core}${mark}${trail}` };
}
